import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import classnames from 'classnames';
import {FormattedMessage} from 'react-intl';
import {clearAllBodyScrollLocks} from 'body-scroll-lock';

import {SITE_URLS} from 'features/search/constants';
import {post} from 'features/services/utils/request/request';
import {capitalizeFirstLetter} from 'features/services/utils/helpers';
import {getFormattedMessage} from 'features/services/utils/language-helper';
import {sanitizeHtml} from 'features/services/utils/sanitize-html';
import {getShowingEnd, getShowingStart} from 'features/services/utils/date-helpers';
import {gtmEventsInteractionCalendarAdd} from 'features/services/googleTagManager/events';
import {Margin, MessageType, HeadlineLevel} from 'features/ui/enums';

import './style.scss';

import {A} from 'components/atoms/LinksAndButtons/A';
import {P} from 'components/atoms/Text/P';
import {Button, ButtonColor, ButtonSize, ButtonState, ButtonType} from 'components/atoms/LinksAndButtons/Button';
import {Counter} from 'components/atoms/FormElements/Counter';
import {Message} from 'components/atoms/Message';
import {useModal} from "features/modal/hooks/useModal";
import {ShowingsType} from "features/search/enums";
import {Showing} from "features/search/types";
import {useAuth} from "features/authentication/hooks/useAuth";
import {useAddShowing, useRemoveShowing} from "features/authentication/services/ShowingService/mutation";
import {AddShowingParams} from "features/authentication/types.services";
import {Headline} from '@atoms/Text/Headline';

interface Props {
    showing: Showing;
    stageId: string;
    stageName: string;
    url: string;
}

export enum ShowingState {
    Idle,
    Booked,
}

export const ShowingsModal: React.FunctionComponent<Props> = ({
    showing, stageId, stageName, url,
}) => {
    const [participants, setParticipants] = useState<number>(0);
    const [showingState, setShowingState] = useState<ShowingState>(ShowingState.Idle);
    const [currentUserShowing, setCurrentUserShowing] = useState<AddShowingParams>({ showingId: '', attendees: 0, stageId: '' });
    const [message, setMessage] = useState<string>('');
    const [_hasSubmited, setHasSubmited] = useState<boolean>(false);
    const unregisterLinkRef = useRef<HTMLAnchorElement>(null);

    const { open, close } = useModal();

    const {user} = useAuth();
    const {mutate: addShowing, isPending} = useAddShowing();
    const {mutate: removeShowing} = useRemoveShowing();

    useEffect(() => {
        if(user && user.showings) {
            const hasShowing = user.showings.filter(userShowing => userShowing.showingId === showing.id);

            if(hasShowing.length > 0) {
                setShowingState(ShowingState.Booked);
                setCurrentUserShowing({
                    ...hasShowing[0],
                    attendees: hasShowing[0].participants
                });
            }
        }
    }, [user]);

    const submitShowing = async () => {
        setHasSubmited(true);

        if(!showing.digitalShowingUrl && participants === 0) {
            setMessage(getFormattedMessage('jm.components.ShowingsModal.howManyError'));
            setHasSubmited(false);
            return;
        }

        const newShowing : AddShowingParams = {
            showingId: showing.id,
            attendees: participants,
            stageId: stageId
        };


        addShowing(newShowing);

        setMessage('');
        setShowingState(ShowingState.Booked);
        setCurrentUserShowing(newShowing);
        setHasSubmited(false);
    };

    const onCountChange = (count: number) => {
        setMessage('');
        setParticipants(count);
    };

    const closeModal = () => {
        close();
    };

    const unregisterVip = async () => {
        removeShowing(showing.id);
        closeModal();
    };

    const createIcsFileFromShowing = async (showing: Showing) => {
        const showingCopy = {...showing};

        const path = window.location.protocol + '//' + window.location.hostname;
        const requstUrl = path + `/api/calendar/createics`;
        showingCopy.stageName = stageName;
        showingCopy.url = url;

        const response = await post(requstUrl, showingCopy);

        if(response){
            response.blob().then(function(myBlob){
                const objectURL = URL.createObjectURL(myBlob);
                const downloadLink = document.createElement("a");
                downloadLink.href = objectURL;
                downloadLink.download = "event.ics";
                return downloadLink.click();
            });
        }
    };

    const onUnregister = (e: React.SyntheticEvent) => {
        e.preventDefault();

        open(
            <div className="showings-modal__unregister-window">
                <Headline level={HeadlineLevel.H3}><FormattedMessage id="jm.components.ShowingsModal.cancelBookingHeader" /></Headline>
                {!showing.digitalShowingUrl
                    ? <P>
                        <FormattedMessage id="jm.components.ShowingsModal.cancelBookingDescription" /> {showing.address}, {showing.postalCode}, {showing.city}, {showing.startDate ? getShowingStart(new Date(showing.startDate)) : getShowingStart(new Date(showing.startDateTimeStamp * 1000))} - <span className="no-phone"><a className="no-styles">{showing.endDate ? getShowingEnd(new Date(showing.endDate)) : getShowingEnd(new Date(showing.endDateTimeStamp * 1000))}</a></span>?
                    </P>
                    : <P>
                        <FormattedMessage id="jm.components.ShowingsModal.cancelBookingDescription" />{stageName}
                    </P>
                }
                <div className="showings-modal__unregister-buttons">
                    <Button
                        onClick={unregisterVip}
                        buttonColor={ButtonColor.CTA}
                        buttonSize={ButtonSize.WIDE}
                        buttonType={ButtonType.DEFAULT}
                    >
                        <FormattedMessage id="jm.components.ShowingsModal.cancelBookingHeader" />
                    </Button>
                    <Button
                        onClick={closeModal}
                        buttonColor={ButtonColor.DEFAULT}
                        buttonSize={ButtonSize.WIDE}
                        buttonType={ButtonType.GHOST}
                    >
                        <FormattedMessage id="jm.components.ShowingsModal.cancel" />
                    </Button>
                </div>
            </div>,
            { fullscreen: false, ariaLabel: getFormattedMessage('jm.components.ShowingsModal.cancelBookingHeader'), triggerElementId: 'showingsUnregisterButton' }
        );

    };

    const isVipShowing = showing.type === ShowingsType.VIP && user?.isVip;

    useEffect(() => {
        clearAllBodyScrollLocks()
    }, []);

    const classes = classnames('showings-modal', {
        'showings-modal--vip': isVipShowing,
    });

    const mapUrl = showing.address && showing.postalCode && showing.city
        ? `https://www.google.com/maps/dir//${showing.address} ${showing.postalCode} ${showing.city}`
        : `https://www.google.com/maps?q=${showing.latitude},${showing.longitude}`;

    return (
        <div className={classes}>
            <Headline level={HeadlineLevel.H2} center margin={Margin.L}>
                {isVipShowing
                    ? (showingState === ShowingState.Idle && !showing.digitalShowingUrl)
                        ? <FormattedMessage id="jm.components.ShowingsModal.vipReserve" />
                        : <FormattedMessage id="jm.components.ShowingsModal.welcomeShowingVip" />
                    : <FormattedMessage id="jm.components.ShowingsModal.welcomeShowing" />}
            </Headline>

            <div className="showings-modal__info">
                <div className="showings-modal__date"  x-ms-format-detection="none">
                    <P>
                        <strong><FormattedMessage id="jm.components.ShowingsModal.date" /></strong><br />
                        <span x-ms-format-detection="none">
                            { !showing.startDate && !showing.endDate && `${capitalizeFirstLetter(getShowingStart(new Date(showing.startDateTimeStamp * 1000)))} - ${getShowingEnd(new Date(showing.endDateTimeStamp * 1000))}` }
                            { showing.startDate && showing.endDate && `${capitalizeFirstLetter(getShowingStart(new Date(showing.startDate)))} - ${getShowingEnd(new Date(showing.endDate))}` }
                        </span><br />
                        {(isVipShowing && showingState === ShowingState.Booked || !isVipShowing || showing.digitalShowingUrl) && 
                            <A 
                                href={"#"} 
                                onClick={() => createIcsFileFromShowing(showing)} 
                                attachedEvent={gtmEventsInteractionCalendarAdd()} 
                                title={getFormattedMessage('jm.components.ShowingsModal.saveToCalendar')} 
                            >
                                <FormattedMessage id="jm.components.ShowingsModal.saveToCalendar" />
                            </A>
                        }
                    </P>
                </div>

                {!showing.digitalShowingUrl ?
                    <div className="showings-modal__address">
                            <P>
                                <strong><FormattedMessage id="jm.components.ShowingsModal.showingsAddress" /></strong><br />
                                    <A
                                        href={mapUrl}
                                        target="_blank"
                                        className="notranslate" 
                                        title={showing.address + ', ' + showing.postalCode + ' ' + showing.city} 
                                    >
                                    {showing.address}, {showing.postalCode} {showing.city}
                                </A>
                            </P>
                    </div> :

                    <div className="showings-modal__address">
                        <P>
                            <strong><FormattedMessage id="jm.components.ShowingsModal.showingsAddress" /></strong><br />
                            <A
                                href={showing.digitalShowingUrl} 
                                target="_blank" 
                                className="notranslate" 
                                title={getFormattedMessage('jm.components.Showings.connectToLiveShowing')} 
                            >
                            <FormattedMessage id="jm.components.Showings.connectToLiveShowing"/>
                        </A>
                        </P>
                    </div>
                }
            </div>

            {!isVipShowing &&
                <React.Fragment>
                    <div className="showings-modal__description">
                        <P>{showing.description}</P>
                    </div>

                    {!showing.digitalShowingUrl && showing.directions &&
                        <div className="showings-modal__directions">
                            <P>
                                <strong><FormattedMessage id="jm.components.ShowingsModal.directions" /></strong><br />
                                {showing.directions}
                            </P>
                        </div>
                    }
                </React.Fragment>
            }

            {isVipShowing &&
                <div className="showings-modal__description">
                    <P>
                        <FormattedMessage id="jm.components.ShowingsModal.vipDescription" /> {stageName}
                    </P>
                    <div>
                        <A 
                            href={SITE_URLS.mypagesVip} 
                            target="_blank" 
                            title={getFormattedMessage('jm.components.ShowingsModal.vipReadMore')} 
                        >
                            <FormattedMessage id="jm.components.ShowingsModal.vipReadMore" />
                        </A>
                    </div>
                </div>
            }

            {!showing.digitalShowingUrl && showing.directions && isVipShowing &&
                <div className="showings-modal__directions">
                    <P>
                        <strong><FormattedMessage id="jm.components.ShowingsModal.directions" /></strong><br />
                        {showing.directions}
                    </P>
                </div>
            }

            {!showing.digitalShowingUrl && isVipShowing &&
                <div className="showings-modal__vip-form">
                    <strong><FormattedMessage id="jm.components.ShowingsModal.howMany" /></strong>

                    {showingState === ShowingState.Idle &&
                        <React.Fragment>
                            <P>
                                <FormattedMessage id="jm.components.ShowingsModal.howManyDescription" />
                            </P>
                            {message &&
                                <Message variant={MessageType.ERROR}>{message}</Message>
                            }
                            <Counter
                                id={getFormattedMessage('jm.components.ShowingsModal.howManyDescription')}
                                onChange={onCountChange}
                                value={participants}
                                minValue={0}
                            />
                        </React.Fragment>
                    }

                    {showingState === ShowingState.Booked &&
                        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(`<strong>${currentUserShowing.attendees}</strong> <FormattedMessage id="jm.components.ShowingsModal.people" />`) }} />
                    }

                    {showingState === ShowingState.Idle &&
                        <div className="showings-modal__button-container">
                            <Button 
                                onClick={submitShowing} 
                                buttonState={isPending
                                    ? ButtonState.LOADING
                                    : ButtonState.DEFAULT
                                } 
                                buttonColor={ButtonColor.CTA} 
                                buttonSize={ButtonSize.DEFAULT} 
                                buttonType={ButtonType.DEFAULT} 
                            >
                                <FormattedMessage id="jm.components.ShowingsModal.sendBooking" />
                            </Button>
                        </div>
                    }

                    {showingState === ShowingState.Booked &&
                        <div className="showings-modal__unregister">
                            <A
                                href="#"
                                onClick={onUnregister}
                                data-test-id="showing-unregister"
                                ref={unregisterLinkRef}
                                id='showingsUnregisterButton' 
                                title={getFormattedMessage('jm.components.ShowingsModal.unregister')} 
                            >
                                <FormattedMessage id="jm.components.ShowingsModal.unregister" />
                            </A>
                        </div>
                    }
                </div>
            }
        </div>
    )
};
